import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllDrinks,
  getSingleDrink,
  createDrink,
  updateDrink,
  deleteDrink,
  websiteOrderingAvailableItemsToggle,
} from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks

export const fetchAllDrinks = createAsyncThunk(
  "drinks/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllDrinks(restaurant_id);
    if (res.status === "success") return res.data.drinks;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleDrink = createAsyncThunk(
  "drinks/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleDrink(id);
    if (res.status === "success") return res.data.drink;
    return rejectWithValue(res.message);
  }
);

export const createNewDrink = createAsyncThunk(
  "drinks/create",
  async (data, { rejectWithValue }) => {
    const res = await createDrink(data);
    if (res.status === "success") return res.data.drink;
    return rejectWithValue(res.message);
  }
);

export const updateExistingDrink = createAsyncThunk(
  "drinks/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateDrink(id, data);
    if (res.status === "success") return res.data.drink;
    return rejectWithValue(res.message);
  }
);

export const updateAvailableToggle = createAsyncThunk("drinks/toggle", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

export const deleteDrinkById = createAsyncThunk(
  "drinks/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteDrink(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  drinks: [],
  singleDrink: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const drinkSlice = createSlice({
  name: "drinks",
  initialState,
  reducers: {
    clearDrinkMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllDrinks.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllDrinks.fulfilled, (state, action) => {
        state.getLoading = false;
        state.drinks = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllDrinks.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleDrink.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleDrink.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleDrink = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleDrink.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewDrink.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewDrink.fulfilled, (state, action) => {
        state.createLoading = false;
        state.drinks.push(action.payload);
        state.success = "Drink created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewDrink.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingDrink.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingDrink.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.drinks = state.drinks.map((drink) =>
          drink.id === action.payload.id ? action.payload : drink
        );
        state.success = "Drink updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingDrink.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website / QR code availability
      .addCase(updateAvailableToggle.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateAvailableToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.drinks = state.drinks.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateAvailableToggle.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteDrinkById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteDrinkById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.drinks = state.drinks.filter(
          (drink) => drink.id !== action.payload
        );
        state.success = "Drink deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteDrinkById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearDrinkMessages } = drinkSlice.actions;
export default drinkSlice.reducer;
